import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  ReferenceObject,
  referenceObjects,
  commonScreenSizes,
  CommonScreenSize,
  computePPIFromWidthMM,
  computePPIFromDiameter,
  computePPIFromPhoneWidth,
  saveCalibration,
  CalibrationSource,
  calculateCssPixelsPerInch,
  CREDIT_CARD_WIDTH_MM,
  CREDIT_CARD_HEIGHT_MM,
  phoneReferences,
  getPhoneBrands,
  getPhonesByBrand,
} from '../utils/screenDetection';

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

type Step = 'choose' | 'align-card' | 'align-coin' | 'align-phone' | 'select-size' | 'verify';

interface CalibrationOverlayProps {
  locale: 'en' | 'zh' | 'nl';
  onCalibrated: (ppi: number, source: CalibrationSource) => void;
  onCancel: () => void;
  onPreview?: (ppi: number) => void;  // real-time preview while adjusting
}

// ═══════════════════════════════════════════════════════════════
// Localized strings
// ═══════════════════════════════════════════════════════════════

const i18n = {
  en: {
    title: 'Calibrate Your Screen',
    chooseMethod: 'Choose a calibration method',
    cardTitle: 'Bank Card / ID Card',
    cardDesc: 'Most common, recommended',
    coinTitle: 'Coin',
    coinDesc: 'Quick calibration',
    screenTitle: 'Screen Size',
    screenDesc: 'I know my screen size',
    skipTitle: 'Skip',
    skipDesc: 'Use default settings',
    alignCardTitle: 'Align Your Card',
    alignCardInstr:
      'Place a credit card, bank card, or ID card on your screen and drag the right edge until the rectangle matches exactly.',
    alignCoinTitle: 'Align Your Coin',
    alignCoinInstr:
      'Place the selected coin on your screen and drag the right edge until the circle matches exactly.',
    selectCoinType: 'Select your coin:',
    selectSizeTitle: 'Select Your Screen Size',
    selectSizeInstr: 'Choose the screen size that matches your display.',
    laptopSizes: 'Laptop',
    desktopSizes: 'Desktop Monitor',
    verifyTitle: 'Verify Your Calibration',
    verifyInstr:
      'Does this line look like 50 mm (about 2 inches)? Use the slider below to fine-tune if needed.',
    verifyLine: '50 mm',
    fineTune: 'Fine-tune adjustment:',
    confirm: 'Looks Good!',
    confirmSize: 'Confirm',
    correct: 'Correct!',
    skipVerify: 'Skip verification',
    back: 'Back',
    dragTip: '\u2190 Drag the right edge to resize \u2192',
    standard: `Standard: ${CREDIT_CARD_WIDTH_MM} \u00d7 ${CREDIT_CARD_HEIGHT_MM} mm`,
  },
  zh: {
    title: '\u6821\u51c6\u4f60\u7684\u5c4f\u5e55',
    chooseMethod: '\u9009\u62e9\u6821\u51c6\u65b9\u5f0f',
    cardTitle: '\u94f6\u884c\u5361 / \u8eab\u4efd\u8bc1',
    cardDesc: '\u6700\u5e38\u7528\uff0c\u63a8\u8350',
    coinTitle: '\u786c\u5e01',
    coinDesc: '\u5feb\u901f\u6821\u51c6',
    screenTitle: '\u5c4f\u5e55\u5c3a\u5bf8',
    screenDesc: '\u6211\u77e5\u9053\u6211\u7684\u5c4f\u5e55\u5c3a\u5bf8',
    skipTitle: '\u8df3\u8fc7',
    skipDesc: '\u4f7f\u7528\u9ed8\u8ba4\u8bbe\u7f6e',
    alignCardTitle: '\u5bf9\u9f50\u4f60\u7684\u5361\u7247',
    alignCardInstr:
      '\u5c06\u94f6\u884c\u5361\u3001\u4fe1\u7528\u5361\u6216\u8eab\u4efd\u8bc1\u653e\u5728\u5c4f\u5e55\u4e0a\uff0c\u62d6\u52a8\u53f3\u8fb9\u7f18\u76f4\u5230\u77e9\u5f62\u5b8c\u5168\u91cd\u5408\u3002',
    alignCoinTitle: '\u5bf9\u9f50\u4f60\u7684\u786c\u5e01',
    alignCoinInstr:
      '\u5c06\u9009\u5b9a\u7684\u786c\u5e01\u653e\u5728\u5c4f\u5e55\u4e0a\uff0c\u62d6\u52a8\u53f3\u8fb9\u7f18\u76f4\u5230\u5706\u5f62\u5b8c\u5168\u91cd\u5408\u3002',
    selectCoinType: '\u9009\u62e9\u786c\u5e01\u7c7b\u578b:',
    selectSizeTitle: '\u9009\u62e9\u5c4f\u5e55\u5c3a\u5bf8',
    selectSizeInstr: '\u9009\u62e9\u4e0e\u4f60\u7684\u663e\u793a\u5668\u5339\u914d\u7684\u5c4f\u5e55\u5c3a\u5bf8\u3002',
    laptopSizes: '\u7b14\u8bb0\u672c\u7535\u8111',
    desktopSizes: '\u53f0\u5f0f\u663e\u793a\u5668',
    verifyTitle: '\u9a8c\u8bc1\u6821\u51c6\u7ed3\u679c',
    verifyInstr:
      '\u8fd9\u6761\u7ebf\u5e94\u8be5\u662f 50 \u6beb\u7c73\uff08\u7ea6 2 \u82f1\u5bf8\uff09\u3002\u5982\u679c\u4e0d\u5bf9\uff0c\u8bf7\u7528\u4e0b\u65b9\u6ed1\u5757\u5fae\u8c03\u3002',
    verifyLine: '50 \u6beb\u7c73',
    fineTune: '\u5fae\u8c03:',
    confirm: '\u5bf9\u9f50\u4e86\uff01\u786e\u8ba4',
    confirmSize: '\u786e\u8ba4',
    correct: '\u6b63\u786e\uff01',
    skipVerify: '\u8df3\u8fc7\u9a8c\u8bc1',
    back: '\u8fd4\u56de',
    dragTip: '\u2190 \u62d6\u52a8\u53f3\u8fb9\u7f18\u8c03\u6574\u5927\u5c0f \u2192',
    standard: `\u6807\u51c6\u5c3a\u5bf8: ${CREDIT_CARD_WIDTH_MM} \u00d7 ${CREDIT_CARD_HEIGHT_MM} mm`,
  },
  nl: {
    title: 'Kalibreer Uw Scherm',
    chooseMethod: 'Kies een kalibratiemethode',
    cardTitle: 'Bankpas / ID-kaart',
    cardDesc: 'Meest gebruikt, aanbevolen',
    coinTitle: 'Munt',
    coinDesc: 'Snelle kalibratie',
    screenTitle: 'Schermgrootte',
    screenDesc: 'Ik ken mijn schermgrootte',
    skipTitle: 'Overslaan',
    skipDesc: 'Standaardinstellingen gebruiken',
    alignCardTitle: 'Lijn Uw Kaart Uit',
    alignCardInstr:
      'Plaats een bankpas of creditcard op uw scherm en sleep de rechterrand totdat de rechthoek exact overeenkomt.',
    alignCoinTitle: 'Lijn Uw Munt Uit',
    alignCoinInstr:
      'Plaats de geselecteerde munt op uw scherm en sleep de rechterrand totdat de cirkel exact overeenkomt.',
    selectCoinType: 'Selecteer uw munt:',
    selectSizeTitle: 'Selecteer Uw Schermgrootte',
    selectSizeInstr: 'Kies de schermgrootte die overeenkomt met uw beeldscherm.',
    laptopSizes: 'Laptop',
    desktopSizes: 'Desktopmonitor',
    verifyTitle: 'Verifieer Uw Kalibratie',
    verifyInstr:
      'Deze lijn moet precies 50 mm (ongeveer 2 inch) zijn. Gebruik de schuifregelaar om aan te passen.',
    verifyLine: '50 mm',
    fineTune: 'Fijnafstemming:',
    confirm: 'Ziet er goed uit!',
    confirmSize: 'Bevestigen',
    correct: 'Correct!',
    skipVerify: 'Verificatie overslaan',
    back: 'Terug',
    dragTip: '\u2190 Sleep de rechterrand om te verkleinen/vergroten \u2192',
    standard: `Standaard: ${CREDIT_CARD_WIDTH_MM} \u00d7 ${CREDIT_CARD_HEIGHT_MM} mm`,
  },
};

// ═══════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════

const CalibrationOverlay: React.FC<CalibrationOverlayProps> = ({
  locale,
  onCalibrated,
  onCancel,
  onPreview,
}) => {
  const s = i18n[locale] || i18n.en;

  // ─── Step state ───
  const [step, setStep] = useState<Step>('choose');

  // ─── Card calibration state ───
  const [cardWidth, setCardWidth] = useState(290);
  const cardHeight = cardWidth * (CREDIT_CARD_HEIGHT_MM / CREDIT_CARD_WIDTH_MM);

  // ─── Coin calibration state ───
  const coins = useMemo(
    () => referenceObjects.filter((o) => o.type === 'circle'),
    []
  );
  const [selectedCoinId, setSelectedCoinId] = useState<string>(coins[0]?.id ?? '');
  const selectedCoin = useMemo(
    () => coins.find((c) => c.id === selectedCoinId) ?? coins[0],
    [coins, selectedCoinId]
  );
  const [coinDiameter, setCoinDiameter] = useState(80);

  // ─── Verification state ───
  const [tentativePPI, setTentativePPI] = useState(96);
  const [tentativeSource, setTentativeSource] = useState<CalibrationSource>('calibration');
  const [finetuneMultiplier, setFinetuneMultiplier] = useState(1.0);

  // ─── Phone calibration state ───
  const brands = useMemo(() => getPhoneBrands(), []);
  const [selectedBrand, setSelectedBrand] = useState(brands[0] || 'Apple');
  const brandPhones = useMemo(() => getPhonesByBrand(selectedBrand), [selectedBrand]);
  const [selectedPhoneId, setSelectedPhoneId] = useState(brandPhones[0]?.id ?? '');
  const selectedPhone = useMemo(
    () => phoneReferences.find((p) => p.id === selectedPhoneId) ?? brandPhones[0],
    [brandPhones, selectedPhoneId]
  );
  const [phoneWidth, setPhoneWidth] = useState(200);

  // ─── Drag refs ───
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  // ─── Pointer drag: Card ───
  const handleCardPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      isDragging.current = true;
      startX.current = e.clientX;
      startWidth.current = cardWidth;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [cardWidth]
  );

  const handleCardPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    const dx = e.clientX - startX.current;
    setCardWidth(Math.max(100, Math.min(800, startWidth.current + dx)));
  }, []);

  const handleCardPointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // ─── Pointer drag: Coin ───
  const handleCoinPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      isDragging.current = true;
      startX.current = e.clientX;
      startWidth.current = coinDiameter;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [coinDiameter]
  );

  const handleCoinPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    const dx = e.clientX - startX.current;
    setCoinDiameter(Math.max(40, Math.min(400, startWidth.current + dx)));
  }, []);

  const handleCoinPointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // ─── Pointer drag: Phone ───
  const handlePhonePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      isDragging.current = true;
      startX.current = e.clientX;
      startWidth.current = phoneWidth;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [phoneWidth]
  );

  const handlePhonePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    const dx = e.clientX - startX.current;
    setPhoneWidth(Math.max(80, Math.min(500, startWidth.current + dx)));
  }, []);

  const handlePhonePointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // ─── Helpers ───
  const getLabel = useCallback(
    (obj: ReferenceObject) => {
      if (locale === 'zh') return obj.labelZH;
      if (locale === 'nl') return obj.labelNL;
      return obj.labelEN;
    },
    [locale]
  );

  // ─── Confirm: Card ───
  const handleCardConfirm = () => {
    const ppi = computePPIFromWidthMM(cardWidth, CREDIT_CARD_WIDTH_MM);
    setTentativePPI(ppi);
    setTentativeSource('calibration');
    setFinetuneMultiplier(1.0);
    onPreview?.(ppi);
    setStep('verify');
  };

  // ─── Confirm: Coin ───
  const handleCoinConfirm = () => {
    if (!selectedCoin) return;
    const ppi = computePPIFromDiameter(coinDiameter, selectedCoin.diameterMM);
    setTentativePPI(ppi);
    setTentativeSource('calibration');
    setFinetuneMultiplier(1.0);
    onPreview?.(ppi);
    setStep('verify');
  };

  // ─── Confirm: Phone ───
  const handlePhoneConfirm = () => {
    if (!selectedPhone) return;
    const ppi = computePPIFromPhoneWidth(phoneWidth, selectedPhone.widthMM);
    setTentativePPI(ppi);
    setTentativeSource('calibration');
    setFinetuneMultiplier(1.0);
    onPreview?.(ppi);
    setStep('verify');
  };

  // ─── Confirm: Screen size ───
  const handleScreenSizeSelect = (size: CommonScreenSize) => {
    const ppi = calculateCssPixelsPerInch(size.diagonal);
    setTentativePPI(ppi);
    setTentativeSource('screen-size');
    setFinetuneMultiplier(1.0);
    onPreview?.(ppi);
    setStep('verify');
  };

  // ─── Skip (default fallback) — guess screen size by resolution ───
  const handleSkip = () => {
    // Heuristic: estimate diagonal from screen resolution
    const sw = window.screen.width;
    const sh = window.screen.height;
    const maxDim = Math.max(sw, sh);
    // Rough guess: <1600px → 14", <2000px → 15.6", <2600px → 24", else 27"
    let guessedDiag = 15.6;
    if (maxDim <= 1440) guessedDiag = 13.3;
    else if (maxDim <= 1600) guessedDiag = 14;
    else if (maxDim <= 2000) guessedDiag = 15.6;
    else if (maxDim <= 2600) guessedDiag = 24;
    else guessedDiag = 27;
    const ppi = calculateCssPixelsPerInch(guessedDiag);
    saveCalibration(ppi, 'default');
    onCalibrated(ppi, 'default');
  };

  // ─── Verification: Correct ───
  const handleVerifyConfirm = () => {
    const finalPPI = tentativePPI * finetuneMultiplier;
    saveCalibration(finalPPI, 'calibration-verified');
    onCalibrated(finalPPI, 'calibration-verified');
  };

  // ─── Verification: Skip ───
  const handleSkipVerify = () => {
    const finalPPI = tentativePPI * finetuneMultiplier;
    saveCalibration(finalPPI, tentativeSource);
    onCalibrated(finalPPI, tentativeSource);
  };

  // ─── Verification line calculation ───
  const effectivePPI = tentativePPI * finetuneMultiplier;
  const pxPerMM = effectivePPI / 25.4;
  const verifyLinePx = pxPerMM * 50; // 50 mm

  // ═══════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-auto overflow-hidden">
        {/* ═══════════════════════════════════════════════
            Step 1 — Choose calibration method
           ═══════════════════════════════════════════════ */}
        {step === 'choose' && (
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-1 flex items-center gap-2">
              <span className="text-2xl">📏</span>
              {s.title}
            </h2>
            <p className="text-sm text-gray-500 mb-5">{s.chooseMethod}</p>

            <div className="grid grid-cols-1 gap-3 mb-3">
              {/* Screen size — primary recommended option (easiest, no physical object needed) */}
              <button
                onClick={() => setStep('select-size')}
                className="group relative flex items-center gap-4 p-4 rounded-xl border-2 border-purple-300 bg-purple-50/50 hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
              >
                <span className="text-3xl">🖥️</span>
                <div>
                  <span className="text-sm font-semibold text-gray-800 group-hover:text-purple-700">
                    {s.screenTitle}
                  </span>
                  <span className="block text-xs text-gray-400">{s.screenDesc}</span>
                </div>
                <span className="absolute top-2 right-2 bg-purple-100 text-purple-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  ★ {locale === 'zh' ? '推荐' : locale === 'nl' ? 'Aanbevolen' : 'Recommended'}
                </span>
              </button>

              {/* Secondary options row */}
              <div className="grid grid-cols-3 gap-2">
                {/* Card */}
                <button
                  onClick={() => setStep('align-card')}
                  className="group flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50/50 transition-all text-center"
                >
                  <span className="text-2xl">💳</span>
                  <span className="text-xs font-semibold text-gray-800 group-hover:text-blue-700">
                    {s.cardTitle}
                  </span>
                </button>

                {/* Phone as reference */}
                <button
                  onClick={() => setStep('align-phone')}
                  className="group flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-gray-200 hover:border-green-400 hover:bg-green-50/50 transition-all text-center"
                >
                  <span className="text-2xl">📱</span>
                  <span className="text-xs font-semibold text-gray-800 group-hover:text-green-700">
                    {locale === 'zh' ? '手机' : locale === 'nl' ? 'Telefoon' : 'Phone'}
                  </span>
                </button>

                {/* Coin */}
                <button
                  onClick={() => setStep('align-coin')}
                  className="group flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-gray-200 hover:border-amber-400 hover:bg-amber-50/50 transition-all text-center"
                >
                  <span className="text-2xl">🪙</span>
                  <span className="text-xs font-semibold text-gray-800 group-hover:text-amber-700">
                    {s.coinTitle}
                  </span>
                </button>
              </div>
            </div>

            {/* Skip button — separate, full width */}
            <button
              onClick={handleSkip}
              className="w-full py-2.5 px-4 rounded-xl text-sm font-medium text-gray-500 bg-gray-50 hover:bg-gray-100 transition-colors text-center"
            >
              ⏭️ {s.skipTitle} — {s.skipDesc}
            </button>
          </div>
        )}

        {/* ═══════════════════════════════════════════════
            Step 2a — Card alignment
           ══════════════════════════��════════════════════ */}
        {step === 'align-card' && (
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
              <span>💳</span>
              {s.alignCardTitle}
            </h2>
            <p className="text-sm text-gray-500 mb-1">{s.alignCardInstr}</p>
            <p className="text-xs text-gray-400 mb-4">{s.standard}</p>

            {/* Card rectangle */}
            <div className="flex justify-center mb-3">
              <div
                className="relative select-none"
                style={{ width: `${cardWidth}px`, height: `${cardHeight}px` }}
              >
                {/* Card body */}
                <div
                  className="w-full h-full rounded-lg border-2 border-blue-500 bg-blue-50/50"
                  style={{
                    backgroundImage: `
                      linear-gradient(135deg, rgba(59,130,246,0.08) 25%, transparent 25%),
                      linear-gradient(225deg, rgba(59,130,246,0.08) 25%, transparent 25%),
                      linear-gradient(45deg, rgba(59,130,246,0.08) 25%, transparent 25%),
                      linear-gradient(315deg, rgba(59,130,246,0.08) 25%, transparent 25%)
                    `,
                    backgroundSize: '20px 20px',
                    backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                  }}
                >
                  {/* Fake card chip */}
                  <div className="absolute top-3 left-4 w-8 h-6 rounded border border-yellow-400 bg-yellow-100/60" />
                  {/* Card label text */}
                  <div className="absolute bottom-2 left-4 text-xs font-mono text-blue-400 tracking-widest">
                    CALIBRATION
                  </div>
                  {/* Size display */}
                  <div className="absolute bottom-2 right-3 text-[10px] text-blue-300">
                    {CREDIT_CARD_WIDTH_MM} × {CREDIT_CARD_HEIGHT_MM} mm
                  </div>
                </div>

                {/* Drag handle (right edge) */}
                <div
                  className="absolute top-0 -right-3 w-6 h-full cursor-ew-resize flex items-center justify-center"
                  onPointerDown={handleCardPointerDown}
                  onPointerMove={handleCardPointerMove}
                  onPointerUp={handleCardPointerUp}
                  style={{ touchAction: 'none' }}
                >
                  <div className="w-1.5 h-12 bg-blue-500 rounded-full shadow-md" />
                </div>
              </div>
            </div>

            <p className="text-center text-xs text-gray-400 mb-5">{s.dragTip}</p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setStep('choose')}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                {s.back}
              </button>
              <button
                onClick={handleCardConfirm}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                {s.confirm} ✓
              </button>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════
            Step 2b — Coin alignment
           ═══════════════════════════════════════════════ */}
        {step === 'align-coin' && (
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
              <span>🪙</span>
              {s.alignCoinTitle}
            </h2>
            <p className="text-sm text-gray-500 mb-3">{s.alignCoinInstr}</p>

            {/* Coin type selector — horizontally scrollable */}
            <p className="text-xs font-medium text-gray-500 mb-2">{s.selectCoinType}</p>
            <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1">
              {coins.map((coin) => (
                <button
                  key={coin.id}
                  onClick={() => {
                    setSelectedCoinId(coin.id);
                    setCoinDiameter(Math.round(coin.diameterMM * 3.2));
                  }}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-all whitespace-nowrap ${
                    selectedCoinId === coin.id
                      ? 'border-amber-400 bg-amber-50 text-amber-800'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {getLabel(coin)}
                </button>
              ))}
            </div>

            {/* Coin circle */}
            <div className="flex justify-center mb-3">
              <div
                className="relative select-none"
                style={{ width: `${coinDiameter}px`, height: `${coinDiameter}px` }}
              >
                {/* Circle body */}
                <div
                  className="w-full h-full border-2 border-amber-500 bg-amber-50/50 flex flex-col items-center justify-center"
                  style={{ borderRadius: '50%' }}
                >
                  <span className="text-2xl leading-none mb-0.5">🪙</span>
                  {coinDiameter > 60 && selectedCoin && (
                    <>
                      <span className="text-[10px] text-amber-700 font-medium leading-tight text-center px-2">
                        {getLabel(selectedCoin)}
                      </span>
                      <span className="text-[9px] text-amber-400 leading-tight">
                        ⌀ {selectedCoin.diameterMM} mm
                      </span>
                    </>
                  )}
                </div>

                {/* Drag handle (right edge) */}
                <div
                  className="absolute top-0 -right-3 w-6 h-full cursor-ew-resize flex items-center justify-center"
                  onPointerDown={handleCoinPointerDown}
                  onPointerMove={handleCoinPointerMove}
                  onPointerUp={handleCoinPointerUp}
                  style={{ touchAction: 'none' }}
                >
                  <div className="w-1.5 h-12 bg-amber-500 rounded-full shadow-md" />
                </div>
              </div>
            </div>

            <p className="text-center text-xs text-gray-400 mb-5">{s.dragTip}</p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setStep('choose')}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                {s.back}
              </button>
              <button
                onClick={handleCoinConfirm}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 transition-colors"
              >
                {s.confirm} ✓
              </button>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════
            Step 2c — Phone as reference
           ═══════════════════════════════════════════════ */}
        {step === 'align-phone' && (
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
              <span>📱</span>
              {locale === 'zh' ? '用手机校准' : locale === 'nl' ? 'Kalibreer met telefoon' : 'Calibrate with Phone'}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {locale === 'zh'
                ? '选择你的手机型号，然后将手机横放在屏幕上，拖动右边缘使矩形与手机完全重合。'
                : locale === 'nl'
                ? 'Selecteer uw telefoonmodel, leg uw telefoon horizontaal op het scherm en sleep de rechterrand totdat deze exact overeenkomt.'
                : 'Select your phone model, place it horizontally on the screen, and drag the right edge until the rectangle matches exactly.'}
            </p>

            {/* Brand selector */}
            <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
              {brands.map((brand) => (
                <button
                  key={brand}
                  onClick={() => {
                    setSelectedBrand(brand);
                    const phones = getPhonesByBrand(brand);
                    if (phones.length > 0) setSelectedPhoneId(phones[0].id);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                    selectedBrand === brand
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {brand}
                </button>
              ))}
            </div>

            {/* Model selector */}
            <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
              {brandPhones.map((phone) => (
                <button
                  key={phone.id}
                  onClick={() => setSelectedPhoneId(phone.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                    selectedPhoneId === phone.id
                      ? 'bg-green-100 border-2 border-green-400 text-green-700'
                      : 'bg-gray-50 border-2 border-gray-200 text-gray-600 hover:border-green-300'
                  }`}
                >
                  {phone.model}
                </button>
              ))}
            </div>

            {/* Phone rectangle — horizontal (landscape) orientation */}
            {selectedPhone && (
              <div className="flex flex-col items-center mb-4">
                <div
                  className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border-2 border-gray-600 shadow-lg"
                  style={{
                    width: `${phoneWidth}px`,
                    height: `${phoneWidth * (selectedPhone.widthMM / selectedPhone.heightMM)}px`,
                  }}
                  onPointerMove={handlePhonePointerMove}
                  onPointerUp={handlePhonePointerUp}
                >
                  {/* Phone screen area */}
                  <div className="absolute inset-2 bg-gray-700 rounded-lg flex items-center justify-center">
                    <span className="text-gray-400 text-xs text-center">
                      {selectedPhone.model}<br/>
                      {selectedPhone.heightMM} × {selectedPhone.widthMM} mm
                    </span>
                  </div>

                  {/* Drag handle on right edge */}
                  <div
                    className="absolute top-0 -right-4 w-8 h-full flex items-center justify-center cursor-ew-resize touch-none"
                    onPointerDown={handlePhonePointerDown}
                  >
                    <div className="w-1.5 h-10 bg-green-500 rounded-full shadow-md" />
                  </div>
                </div>

                {/* Drag tip */}
                <p className="text-xs text-gray-400 mt-3">
                  {locale === 'zh' ? '← 拖动右边缘调整大小 →' : s.dragTip}
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setStep('choose')}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                {s.back}
              </button>
              <button
                onClick={handlePhoneConfirm}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-bold text-white bg-green-600 hover:bg-green-700 transition-colors"
              >
                {s.confirm}
              </button>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════
            Step 2d — Screen size selection
           ═══════════════════════════════════════════════ */}
        {step === 'select-size' && (
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
              <span>🖥️</span>
              {s.selectSizeTitle}
            </h2>
            <p className="text-sm text-gray-500 mb-5">{s.selectSizeInstr}</p>

            {/* Laptop sizes */}
            <div className="mb-4">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <span>💻</span> {s.laptopSizes}
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {commonScreenSizes
                  .filter((sz) => sz.category === 'laptop')
                  .map((sz) => (
                    <button
                      key={sz.diagonal}
                      onClick={() => handleScreenSizeSelect(sz)}
                      className="py-2.5 px-3 rounded-lg text-left border-2 border-gray-200 bg-white hover:border-purple-400 hover:bg-purple-50 transition-all"
                    >
                      <div className="text-sm font-bold text-gray-800">{sz.label}</div>
                      <div className="text-[10px] text-gray-400 mt-0.5 truncate">{locale === 'zh' ? sz.hintZH : sz.hint}</div>
                    </button>
                  ))}
              </div>
            </div>

            {/* Desktop sizes */}
            <div className="mb-5">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <span>🖥️</span> {s.desktopSizes}
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {commonScreenSizes
                  .filter((sz) => sz.category === 'desktop')
                  .map((sz) => (
                    <button
                      key={sz.diagonal}
                      onClick={() => handleScreenSizeSelect(sz)}
                      className="py-2.5 px-3 rounded-lg text-left border-2 border-gray-200 bg-white hover:border-purple-400 hover:bg-purple-50 transition-all"
                    >
                      <div className="text-sm font-bold text-gray-800">{sz.label}</div>
                      <div className="text-[10px] text-gray-400 mt-0.5 truncate">{locale === 'zh' ? sz.hintZH : sz.hint}</div>
                    </button>
                  ))}
              </div>
            </div>

            {/* Back button */}
            <button
              onClick={() => setStep('choose')}
              className="w-full py-2.5 px-4 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              {s.back}
            </button>
          </div>
        )}

        {/* ═══════════════════════════════════════════════
            Step 3 — Verification
           ═══════════════════════════════════════════════ */}
        {step === 'verify' && (
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
              <span>✅</span>
              {s.verifyTitle}
            </h2>
            <p className="text-sm text-gray-500 mb-5">{s.verifyInstr}</p>

            {/* Verification line: 50 mm */}
            <div className="flex flex-col items-center mb-5">
              <div className="relative mb-1" style={{ width: `${verifyLinePx}px` }}>
                {/* Horizontal bar */}
                <div className="h-0.5 bg-emerald-500 w-full rounded-full" />
                {/* Left end cap */}
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-3 bg-emerald-500 rounded-full" />
                {/* Right end cap */}
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0.5 h-3 bg-emerald-500 rounded-full" />
              </div>
              <span className="text-xs font-semibold text-emerald-600">{s.verifyLine}</span>
            </div>

            {/* Fine-tune slider */}
            <div className="mb-5">
              <label className="block text-xs font-medium text-gray-500 mb-2">
                {s.fineTune}{' '}
                <span className="text-gray-400">
                  ({((finetuneMultiplier - 1) * 100).toFixed(1)}%)
                </span>
              </label>
              <input
                type="range"
                min="0.9"
                max="1.1"
                step="0.002"
                value={finetuneMultiplier}
                onChange={(e) => {
                  const newMul = parseFloat(e.target.value);
                  setFinetuneMultiplier(newMul);
                  onPreview?.(tentativePPI * newMul);
                }}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
              />
              <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                <span>-10%</span>
                <span>0%</span>
                <span>+10%</span>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mb-3">
              <button
                onClick={() => setStep('choose')}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                {s.back}
              </button>
              <button
                onClick={handleVerifyConfirm}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 transition-colors"
              >
                {s.correct} ✓
              </button>
            </div>

            {/* Skip verification link */}
            <button
              onClick={handleSkipVerify}
              className="w-full text-center text-xs text-gray-400 hover:text-gray-600 transition-colors underline underline-offset-2"
            >
              {s.skipVerify}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalibrationOverlay;
